import React, {useEffect, useRef} from "react";
import {useFormContext} from "react-hook-form";

import {IInputFieldProps} from "@pg-design/inputs";
import {InputField} from "@pg-design/rhf-components-module";

import {DEFAULT_EMAIL_VERIFICATION_ERROR} from "../constants/error_messages";
import {EmailVerificationResponse} from "../types/EmailVerificationResponse";

interface Props extends IInputFieldProps {
    verificationData: EmailVerificationResponse | undefined;
    verificationTrigger: (email: string | null) => void;
    dataTestId?: string;
}

//Email input component with bouncer validation support for react hook form usage
export const EmailInputWithVerification = (props: Props) => {
    const {verificationData, verificationTrigger, dataTestId, className, disabled} = props;
    const {setError, clearErrors, formState} = useFormContext();
    const defaultEmail = formState.defaultValues?.email;
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setEmailError = (message?: string) => {
        setError("email", {type: "onBlur", message: message || DEFAULT_EMAIL_VERIFICATION_ERROR});
    };

    const timeoutClear = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    useEffect(() => {
        return () => {
            timeoutClear();
        };
    }, []);

    useEffect(() => {
        if (!verificationData) {
            return;
        }

        if (!verificationData.valid && verificationData.extra.didyoumean) {
            setEmailError(`${DEFAULT_EMAIL_VERIFICATION_ERROR}, czy chodziło Ci o ${verificationData.extra.didyoumean}`);
            return;
        }

        if (!verificationData.valid) {
            setEmailError();
        }
    }, [verificationData]);

    const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) {
            return;
        }
        if ("email" in formState.errors) {
            clearErrors("email");
        }

        const inputValue = event.currentTarget.value;
        const isEmailFormat = emailRegex.test(inputValue);

        if (isEmailFormat) {
            //prevent triggering validation if email is same as default (usually autofilled from user data)
            const preventVerificationTrigger = typeof defaultEmail === "string" && inputValue === defaultEmail;

            if (preventVerificationTrigger) {
                return;
            }

            verificationTrigger(inputValue);
        } else {
            setEmailError();
        }
    };

    return <InputField name="email" placeholder="E-mail" onBlur={handleBlur} dataTestId={dataTestId} className={className} disabled={disabled} />;
};

const emailRegex = new RegExp(`^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$`);
